import { resolveClient } from './supabase';
import type { SupabaseClient } from '@supabase/supabase-js';

export interface PicklistItem {
  id: string;
  label: string;
  order: number;
  probability?: number;
  is_closed?: boolean;
  is_won?: boolean;
}

export interface Picklist {
  id: string;
  tenant_id: string;
  name: string;
  items: PicklistItem[];
  created_at: string;
  updated_at: string;
}

/**
 * Get a picklist by name for the current tenant
 */
export async function getPicklist(name: string, client?: SupabaseClient): Promise<Picklist | null> {
  const supabase = resolveClient(client);
  const { data, error } = await supabase.from('picklists').select('*').eq('name', name).single();
  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  if (data) {
    data.items = [...data.items].sort((a, b) => (a.order || 0) - (b.order || 0));
  }
  return data as Picklist;
}

/**
 * Get all picklists for the current tenant
 */
export async function getPicklists(client?: SupabaseClient): Promise<Picklist[]> {
  const supabase = resolveClient(client);
  const { data, error } = await supabase.from('picklists').select('*').order('name');
  if (error) throw error;
  return (data || []).map((picklist) => ({
    ...picklist,
    items: [...picklist.items].sort((a, b) => (a.order || 0) - (b.order || 0)),
  })) as Picklist[];
}

/**
 * Update a picklist's items
 */
export async function updatePicklistItems(name: string, items: PicklistItem[], client?: SupabaseClient): Promise<Picklist> {
  const supabase = resolveClient(client);
  const { data, error } = await supabase.from('picklists').update({ items, updated_at: new Date().toISOString() }).eq('name', name).select().single();
  if (error) {
    console.error('updatePicklistItems error:', error);
    throw new Error(`Failed to update picklist: ${error.message}`);
  }
  if (!data) {
    throw new Error(`No picklist found with name "${name}" or update was not permitted`);
  }
  return data as Picklist;
}
